/**
 * Data layer for `export-permission-csv-xlsx-per-role`. Thin typed fetch wrappers over /api/v1/export-permission-csv-xlsx-per-role.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface ExportPermissionCsvXlsxPerRoleRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListExportPermissionCsvXlsxPerRoleResponse {
	count: number;
	items: ExportPermissionCsvXlsxPerRoleRow[];
}

export interface CreateExportPermissionCsvXlsxPerRoleBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/export-permission-csv-xlsx-per-role";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListExportPermissionCsvXlsxPerRoleResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListExportPermissionCsvXlsxPerRoleResponse>(res);
}

export async function create(body: CreateExportPermissionCsvXlsxPerRoleBody): Promise<ExportPermissionCsvXlsxPerRoleRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<ExportPermissionCsvXlsxPerRoleRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const exportPermissionCsvXlsxPerRoleQueryKeys = {
	all: ["export-permission-csv-xlsx-per-role"] as const,
	list: () => [...exportPermissionCsvXlsxPerRoleQueryKeys.all] as const,
	detail: (id: string) => [...exportPermissionCsvXlsxPerRoleQueryKeys.all, id] as const,
};
